import { Box, Checkbox, makeStyles, Menu, MenuItem, MenuList } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import React, { useState } from 'react';

import { ConditionsGroupCard } from '@/components/modules/Application/components/PaymentMethod/_shared/components/ConditionsGroupCard/ConditionsGroupCard';
import { usePaymentMethods } from '@/components/modules/Application/components/PaymentMethod/_shared/hooks/usePaymentMethods';
import { ConsumerLoanPaymentMethod } from '@/components/modules/Application/components/PaymentMethod/ConsumerLoan/entities/PaymentMethod/ConsumerLoanPaymentMethod';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { useToggler } from '@/components/shared/hooks/useToggler';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { useScreen } from '@/hooks/useScreen';

interface PaymentMethodConfigurationProps {
  paymentMethodEntity: ConsumerLoanPaymentMethod;
}

export const PaymentMethodConfiguration = ({ paymentMethodEntity }: PaymentMethodConfigurationProps) => {
  const { lg } = useScreen();
  const { getTextIdTranslation } = useTranslation();

  const { isOpen: isDetailOpen, toggle: toggleDetail } = useToggler();
  const { isOpen: isFiltersOpen, toggle: toggleFilters } = useToggler();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleToggleFilters = (e) => {
    setAnchorEl(anchorEl ? null : e.currentTarget);
    toggleFilters();
  };

  return (
    <Box minHeight={lg ? 293 : 0} pb={1} pt={lg ? 3 : 2}>
      <Box display={'flex'} alignItems={'center'} marginBottom={3} gridGap={10}>
        <Box display={'flex'} flexDirection={'column'} flexGrow={1}>
          <Font variant="body1" style={{ fontWeight: 'bold' }}>
            {getTextIdTranslation('PAYMENT_METHODS_TITLE')}
          </Font>
          <Font variant="body2">{getTextIdTranslation('PAYMENT_METHODS_TEXT')}</Font>
        </Box>
        <Box display={'flex'} alignItems={'center'} gridGap={6} flexDirection={lg ? 'row' : 'column'}>
          <StandardButton size={'small'} onClick={toggleDetail} variant="outlined" color="secondary">
            <Box display={'flex'} alignItems={'center'} gridGap={6} flexShrink={0}>
              {getTextIdTranslation('SEE_DETAIL')}
              {isDetailOpen ? <Icon>visibility</Icon> : <Icon>visibility_off</Icon>}
            </Box>
          </StandardButton>
          <StandardButton
            size={'small'}
            onClick={handleToggleFilters}
            variant="outlined"
            color="secondary"
            disabled={!paymentMethodEntity.filters?.length}
          >
            <Box display={'flex'} alignItems={'center'} gridGap={6} flexShrink={0}>
              {getTextIdTranslation('SEE_FILTERS')}
              {isFiltersOpen ? <Icon>filter_alt</Icon> : <Icon>filter_alt_off</Icon>}
            </Box>
          </StandardButton>
          <Menu
            getContentAnchorEl={null}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            open={isFiltersOpen}
            keepMounted
            onClose={handleToggleFilters}
          >
            <Filters paymentMethodEntity={paymentMethodEntity} />
          </Menu>
        </Box>
      </Box>
      <Box display="grid" gridTemplateColumns={'repeat(auto-fill, minmax(330px, 1fr))'} gridGap={24}>
        {paymentMethodEntity.conditionsGroups.map((conditionsGroup, idx) => (
          <ConditionsGroupCard
            key={`${idx}-${conditionsGroup.apr.string}-${conditionsGroup.setupFee?.string}`}
            paymentMethodEntity={paymentMethodEntity}
            conditionsGroup={conditionsGroup}
            isDetailOpen={isDetailOpen}
            selectable
          />
        ))}
      </Box>
      {paymentMethodEntity.conditionsGroups.length === 0 && <NotFoundAlert paymentMethodEntity={paymentMethodEntity} />}
    </Box>
  );
};

const Filters = React.forwardRef<HTMLUListElement, PaymentMethodConfigurationProps>(({ paymentMethodEntity }, ref) => {
  const { getTextIdTranslation } = useTranslation();
  const { updatePaymentMethodEntity } = usePaymentMethods();

  const useStyles = makeStyles({
    root: {
      '&.MuiList-root': {
        paddingBlock: 0,
      },
    },
  });

  const classes = useStyles();

  return (
    <Box>
      <MenuList ref={ref} className={classes.root} style={{ paddingBlock: 'none' }}>
        {paymentMethodEntity.filters?.map((filter) => (
          <MenuItem
            key={filter.type}
            onClick={() => {
              if (!paymentMethodEntity.filters) return;
              const newFilters = paymentMethodEntity.filters.map((f) => {
                if (f.type === filter.type) {
                  return { ...f, isActive: !f.isActive };
                }
                return f;
              });
              updatePaymentMethodEntity(paymentMethodEntity.updateFilters(newFilters));
            }}
          >
            <Checkbox
              style={{ paddingBlock: '2px' }}
              size={'small'}
              checked={filter.isActive}
              onChange={(e) => e.stopPropagation()}
            />
            <Font color="textPrimary" variant="body2">
              {getTextIdTranslation(filter.type)}
            </Font>
          </MenuItem>
        ))}
      </MenuList>
    </Box>
  );
});

export const NotFoundAlert = ({ paymentMethodEntity }: PaymentMethodConfigurationProps) => {
  const { getTextIdTranslation } = useTranslation();

  return (
    <Alert severity="warning">
      {getTextIdTranslation('CONDITIONS_GROUP_NOT_FOUND_START')}{' '}
      <strong>{paymentMethodEntity.availableTerms[0] || 0}</strong> {getTextIdTranslation('AND').toLowerCase()}{' '}
      <strong>
        {paymentMethodEntity.availableTerms[paymentMethodEntity.availableTerms.length - 1] || 0}{' '}
        {getTextIdTranslation('MONTHS').toLowerCase()}
      </strong>
      {'.'}
    </Alert>
  );
};
